import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, XCircle, Clock, Users,
  RefreshCw, Wifi, WifiOff, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import {
  INITIAL_ENTITLEMENTS, INITIAL_ACCESS_EVENTS, INITIAL_PENDING,
  generateLiveEvent,
  type Entitlement, type EntitlementStatus, type AccessEvent, type PendingApproval,
} from '@/data/mockData';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusLabel(s: EntitlementStatus): string {
  const map: Record<EntitlementStatus, string> = {
    active: 'Active', pending_approval: 'Pending Approval',
    scheduled: 'Upcoming', expired: 'Expired', revoked: 'Revoked', denied: 'Denied',
  };
  return map[s];
}

function statusColor(s: EntitlementStatus): string {
  const map: Record<EntitlementStatus, string> = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
    scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    expired: 'bg-gray-100 text-gray-500 border-gray-200',
    revoked: 'bg-red-100 text-red-600 border-red-200',
    denied: 'bg-red-100 text-red-600 border-red-200',
  };
  return map[s];
}

function eventColor(t: string): string {
  if (t === 'granted') return 'text-emerald-600';
  if (t === 'denied') return 'text-red-500';
  if (t === 'revoked') return 'text-orange-500';
  if (t === 'sync_ok') return 'text-blue-500';
  return 'text-muted-foreground';
}

function eventLabel(t: string): string {
  if (t === 'granted') return 'Granted';
  if (t === 'denied') return 'Denied';
  if (t === 'revoked') return 'Revoked';
  if (t === 'sync_ok') return 'Sync OK';
  if (t === 'sync_fail') return 'Sync FAIL';
  return t;
}

function originLabel(o: string): string {
  const map: Record<string, string> = {
    airbnb: 'Airbnb', hostaway: 'Hostaway',
    manual_owner: 'Manual', contractor: 'Contractor', manual_override: 'Emergency',
  };
  return map[o] ?? o;
}

function formatTs(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function timeAgo(ts: string): string {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Approval Card ─────────────────────────────────────────────────────────────

function ApprovalCard({
  approval, onApprove, onDeny,
}: {
  approval: PendingApproval;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
}) {
  const [denyOpen, setDenyOpen] = useState(false);

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
        className="border border-border rounded-xl p-4 bg-card space-y-3 shadow-sm"
        style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 6%, transparent)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {approval.guestName}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {approval.guestEmail} · Requested by Lot {approval.requestorLot} ({approval.requestedBy})
            </div>
          </div>
          <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full shrink-0 font-medium">
            {originLabel(approval.origin)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground mb-0.5">Access Period</div>
            <div className="font-medium text-xs">{formatDate(approval.startTs)} → {formatDate(approval.endTs)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-0.5">Doors</div>
            <div className="flex flex-wrap gap-1">
              {approval.doors.map(d => (
                <span key={d} className="text-xs bg-secondary px-1.5 py-0.5 rounded">{d}</span>
              ))}
            </div>
          </div>
        </div>

        {approval.reason && (
          <div className="bg-muted/60 rounded-lg p-2.5 text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Reason: </span>{approval.reason}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">{timeAgo(approval.requestedAt)}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs"
              onClick={() => setDenyOpen(true)}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Deny
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
              onClick={() => onApprove(approval.id)}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Approve
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Deny confirmation */}
      <Dialog open={denyOpen} onOpenChange={setDenyOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Deny Access Request?</DialogTitle>
            <DialogDescription>
              This will deny access for <strong>{approval.guestName}</strong> and notify the requesting owner (Lot {approval.requestorLot}).
              This action is logged and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDenyOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { onDeny(approval.id); setDenyOpen(false); }}
            >
              Confirm Deny
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Live Activity Feed ────────────────────────────────────────────────────────

function LiveActivityFeed() {
  const [events, setEvents] = useState<AccessEvent[]>(INITIAL_ACCESS_EVENTS);
  const [connected, setConnected] = useState(true);
  const [liveCount, setLiveCount] = useState(0);
  const counterRef = useRef(0);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (connected) {
      interval = setInterval(() => {
        const evt = generateLiveEvent(counterRef.current);
        counterRef.current += 1;
        setLiveCount(c => c + 1);
        setEvents(prev => [evt, ...prev].slice(0, 50));
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [connected]);

  const avgLatency = events
    .filter(e => e.latencyMs > 0)
    .slice(0, 20)
    .reduce((sum, e, _i, arr) => sum + e.latencyMs / arr.length, 0);

  return (
    <div className="space-y-4">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {connected ? (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-emerald-600 font-medium">Live</span>
              <span className="text-muted-foreground">· {liveCount} events received this session</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-red-500" />
              <span className="text-red-500 font-medium">Paused</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Avg latency: <span className="font-mono font-medium text-foreground">{Math.round(avgLatency)}ms</span>
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setConnected(c => !c)}
          >
            {connected ? (
              <><WifiOff className="h-3.5 w-3.5 mr-1" />Pause</>
            ) : (
              <><Wifi className="h-3.5 w-3.5 mr-1" />Resume</>
            )}
          </Button>
        </div>
      </div>

      {/* Event log */}
      <div className="border border-border rounded-xl overflow-hidden">
        <div className="bg-primary px-4 py-2 flex items-center gap-2">
          <span className="text-xs font-mono text-primary-foreground/70">ACCESS_HEARTBEAT</span>
          <span className="text-primary-foreground/50 text-xs">·</span>
          <span className="text-xs text-primary-foreground/70">Canopy Apartments</span>
        </div>
        <div className="overflow-y-auto max-h-96 divide-y divide-border/50">
          <AnimatePresence initial={false}>
            {events.map(evt => (
              <motion.div
                key={evt.id}
                initial={{ opacity: 0, backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
                animate={{ opacity: 1, backgroundColor: 'transparent' }}
                transition={{ duration: 1 }}
                className="flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-muted/40"
              >
                <span className="font-mono text-muted-foreground w-28 shrink-0">
                  {new Date(evt.ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <span className={`w-16 shrink-0 font-semibold ${eventColor(evt.eventType)}`}>
                  {eventLabel(evt.eventType)}
                </span>
                {evt.guestName !== 'System' ? (
                  <>
                    <span className="text-foreground font-medium shrink-0">{evt.guestName}</span>
                    <span className="text-muted-foreground shrink-0">Lot {evt.lotNumber}</span>
                    <span className="text-muted-foreground flex-1 truncate">{evt.door}</span>
                    {evt.latencyMs > 0 && (
                      <span className="font-mono text-muted-foreground shrink-0">{evt.latencyMs}ms</span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground flex-1">Hardware reconciliation — all groups verified</span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// ─── All Entitlements Table ───────────────────────────────────────────────────

function AllEntitlements({ entitlements }: { entitlements: Entitlement[] }) {
  const [filter, setFilter] = useState<EntitlementStatus | 'all'>('all');
  const [lotFilter, setLotFilter] = useState<string>('all');

  const lots = [...new Set(entitlements.map(e => e.lotNumber))].sort();
  const displayed = entitlements.filter(e =>
    (filter === 'all' || e.status === filter) &&
    (lotFilter === 'all' || e.lotNumber === Number(lotFilter)),
  );

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filter} onValueChange={v => setFilter(v as EntitlementStatus | 'all')}>
          <SelectTrigger className="w-44 h-8 text-xs">
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {(['active', 'pending_approval', 'scheduled', 'expired', 'revoked'] as const).map(s => (
              <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={lotFilter} onValueChange={setLotFilter}>
          <SelectTrigger className="w-36 h-8 text-xs">
            <SelectValue placeholder="All Lots" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Lots</SelectItem>
            {lots.map(l => (
              <SelectItem key={l} value={String(l)}>Lot {l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">{displayed.length} record{displayed.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {['Guest', 'Lot', 'Period', 'Doors', 'Source', 'Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-sm text-muted-foreground">No records match.</td>
                </tr>
              )}
              {displayed.map(ent => (
                <tr key={ent.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-sm">{ent.guestName}</div>
                    <div className="text-xs text-muted-foreground">{ent.guestEmail}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">
                    {ent.lotNumber} · {ent.ownerName.split(' ')[0]}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    <div>{formatDate(ent.startTs)}</div>
                    <div>→ {formatDate(ent.endTs)}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {ent.doors.slice(0, 2).map(d => (
                        <span key={d} className="text-xs bg-secondary px-1.5 py-0.5 rounded">{d}</span>
                      ))}
                      {ent.doors.length > 2 && (
                        <span className="text-xs text-muted-foreground">+{ent.doors.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{originLabel(ent.origin)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusColor(ent.status)}`}>
                      {statusLabel(ent.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Reconciliation Panel ────────────────────────────────────────────────────

function ReconciliationPanel() {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(new Date(Date.now() - 15 * 60 * 1000).toISOString());

  function runNow() {
    setRunning(true);
    setTimeout(() => {
      setRunning(false);
      setLastRun(new Date().toISOString());
      toast.success('Reconciliation complete — all access groups verified. No discrepancies found.');
    }, 3500);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Last Run', value: timeAgo(lastRun), sub: formatTs(lastRun), icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Status', value: 'Clean', sub: 'No discrepancies', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Groups Verified', value: '12', sub: 'of 12 access groups', icon: Users, color: 'text-accent', bg: 'bg-accent/10' },
        ].map(card => (
          <Card key={card.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`${card.bg} ${card.color} rounded-xl p-3`}>
                <card.icon className="h-5 w-5" />
              </div>
              <div>
                <div className={`text-xl font-bold ${card.color}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {card.value}
                </div>
                <div className="text-xs text-muted-foreground">{card.label}</div>
                <div className="text-xs text-muted-foreground">{card.sub}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Hardware Sync Status — Canopy Apartments
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={runNow}
              disabled={running}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running…' : 'Run Now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { group: 'AG-CAN-LOT12-GUEST', lot: 12, status: 'synced', credential: 'James Wilson (active)' },
              { group: 'AG-CAN-LOT08-CONTRACTOR', lot: 8, status: 'synced', credential: 'Emma Rodriguez (active)' },
              { group: 'AG-CAN-LOT03-GUEST', lot: 3, status: 'synced', credential: 'Carlos Mendes (active)' },
              { group: 'AG-CAN-LOT15-GUEST', lot: 15, status: 'pending', credential: 'No active credential' },
            ].map(row => (
              <div
                key={row.group}
                className="flex items-center justify-between text-sm py-2 border-b border-border/40 last:border-0"
              >
                <div>
                  <code className="text-xs font-mono text-muted-foreground">{row.group}</code>
                  <div className="text-xs text-muted-foreground mt-0.5">Lot {row.lot} · {row.credential}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                  row.status === 'synced'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                    : 'bg-amber-100 text-amber-700 border-amber-200'
                }`}>
                  {row.status === 'synced' ? 'Synced' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
          {running && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              Verifying all access groups against Inception hardware…
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>(INITIAL_PENDING);
  const [entitlements] = useState<Entitlement[]>(INITIAL_ENTITLEMENTS);

  const activeCount = entitlements.filter(e => e.status === 'active').length;
  const pendingCount = pendingApprovals.length;

  function handleApprove(id: string) {
    const pa = pendingApprovals.find(p => p.id === id);
    if (!pa) return;
    setPendingApprovals(prev => prev.filter(p => p.id !== id));
    toast.success(`Access approved for ${pa.guestName}. Provisioning queued.`);
  }

  function handleDeny(id: string) {
    const pa = pendingApprovals.find(p => p.id === id);
    if (!pa) return;
    setPendingApprovals(prev => prev.filter(p => p.id !== id));
    toast.error(`Access denied for ${pa.guestName}. Owner has been notified.`);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <Toaster richColors position="top-right" />

      {/* Header */}
      <div>
        <h1 style={{ fontFamily: 'Montserrat, sans-serif' }} className="text-2xl font-bold text-foreground">
          Committee Dashboard
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Canopy Apartments — Owners Corporation Administration
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Active Guests', value: activeCount, color: 'text-emerald-600' },
          { label: 'Awaiting Approval', value: pendingCount, color: pendingCount > 0 ? 'text-amber-600' : 'text-muted-foreground' },
          { label: 'Total Entitlements', value: entitlements.length, color: 'text-foreground' },
          { label: 'Avg Latency', value: '312ms', color: 'text-blue-600' },
        ].map(s => (
          <Card key={s.label} className="border-border">
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {s.value}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="approvals">
        <TabsList className="mb-4">
          <TabsTrigger value="approvals" className="relative">
            Pending Approvals
            {pendingCount > 0 && (
              <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full h-4 w-4 inline-flex items-center justify-center font-bold">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="live">Live Activity</TabsTrigger>
          <TabsTrigger value="all">All Entitlements</TabsTrigger>
          <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals">
          <div className="space-y-3">
            {pendingApprovals.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <CheckCircle className="h-10 w-10 mx-auto mb-3 text-emerald-400" />
                <div className="font-medium">All clear</div>
                <div className="text-sm">No pending approvals.</div>
              </div>
            )}
            <AnimatePresence>
              {pendingApprovals.map(pa => (
                <ApprovalCard
                  key={pa.id}
                  approval={pa}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                />
              ))}
            </AnimatePresence>
          </div>
        </TabsContent>

        <TabsContent value="live">
          <LiveActivityFeed />
        </TabsContent>

        <TabsContent value="all">
          <AllEntitlements entitlements={entitlements} />
        </TabsContent>

        <TabsContent value="reconciliation">
          <ReconciliationPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
